import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { useKBArticles } from '../../hooks/useKBArticles';
import { Modal } from '../ui';
import ProgressCheckModal from '../ui/ProgressCheckModal';
import type { KBArticle } from '../../data/kb-articles/types';
import { apiService } from '../../utils/api';
import { type FlowId, resolveResumeFrame, getSummaryFrame, getEntryFrame, FLOW_CONFIG } from '../../utils/frameFlowMap';
import { isFlowEffectivelyComplete } from '../../utils/flowCompletionState';
import KBArticleSection from '../ui/KBArticleSection';
import PlannerDashboardTour from '../ui/PlannerDashboardTour';



// Icon components
const BankIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ''} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2 2 7v2h20V7l-10-5Z" />
    <path d="M4 11h16v9H4v-9Z" />
    <path d="M10 13h4v5h-4v-5Z" fill="#fff" />
  </svg>
);

const HeartIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ''} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21s-6.716-4.686-9.646-9.071C-.205 7.4 1.967 3 6.146 3A5.407 5.407 0 0 1 12 6.326 5.407 5.407 0 0 1 17.854 3c4.179 0 6.351 4.4 3.793 8.929C18.716 16.314 12 21 12 21Z" />
  </svg>
);

const CheckCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className ?? ''} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z" />
    <path d="m10 14 5-5-1.414-1.414L10 11.172 8.414 9.586 7 11l3 3Z" fill="#fff" />
  </svg>
);

// helper render icon
const renderLeftIcon = (type: string, className?: string) => {
  switch (type) {
    case 'bank':
      return <BankIcon className={className} />;
    case 'heart':
      return <HeartIcon className={(className ?? '') + ' text-red-600'} />;
    case 'check':
      return <CheckCircleIcon className={(className ?? '') + ' text-green-700'} />;
    default:
      return null;
  }
};

type FlowProgress = { lastFrame: number | null; updatedAt: string | null };

const Frame140: React.FC = () => {
  const navigate = useNavigate();
  const { session, reconcileFlowCompletion } = useSessionPersistence();
  const { articles } = useKBArticles('frame-140');
  const [selectedArticle, setSelectedArticle] = useState<KBArticle | null>(null);

  const flowIds = useMemo(() => Object.keys(FLOW_CONFIG) as FlowId[], []);
  const isCompleteFlow = (flowId: FlowId) => isFlowEffectivelyComplete({ flowId, session });

  const [activeFilters, setActiveFilters] = useState<string[]>(['Personal', 'Legal', 'Financial']);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [hasShown20PercentModal, setHasShown20PercentModal] = useState(
    () => localStorage.getItem('hasShown20PercentModal') === 'true'
  );

  const [flowProgress, setFlowProgress] = useState<Partial<Record<FlowId, FlowProgress>>>({});
  const [, setFlowProgressLoading] = useState(true);
  const location = useLocation();
  const fromOnboarding = location.state?.fromOnboarding;
  const hasSeenTour = localStorage.getItem('hasSeenDashboardWalkthrough') === 'true';
  const shouldRunTour = !!fromOnboarding && !hasSeenTour;

  // Helper: for dashboard display, if lastFrame is the flow summary but the flow isn't complete,
  // show the last "real" step frame instead.
  const getDashboardProgressFrame = (flowId: FlowId): number | null => {
    const lastFrame = flowProgress[flowId]?.lastFrame ?? null;
    if (lastFrame == null) return null;

    const cfg = FLOW_CONFIG[flowId];
    if (lastFrame !== cfg.summary) return lastFrame;

    const stepFrames = (cfg.steps ?? []).flatMap((s) => s.frames).filter((f) => f !== cfg.summary);
    return stepFrames.length > 0 ? stepFrames[stepFrames.length - 1] : null;
  };

  const getInProgressStepMeta = (flowId: FlowId): { stepText: string; label?: string } | null => {
    const frame = getDashboardProgressFrame(flowId);
    if (frame == null) return null;

    const steps = FLOW_CONFIG[flowId].steps ?? [];
    if (steps.length === 0) return null;

    const isIntroStep = (s: { id?: string }) => (s.id ?? '').includes('intro');
    const stepIdx = steps.findIndex((s) => s.frames.includes(frame));
    if (stepIdx < 0) return null;

    const step = steps[stepIdx];

    if (isIntroStep(step)) {
      return { stepText: 'Intro', label: step.label || undefined };
    }

    const nonIntroSteps = steps.filter((s) => !isIntroStep(s));
    const nonIntroIndex = nonIntroSteps.findIndex((s) => s.id === step.id);

    return {
      stepText: `Step ${nonIntroIndex + 1}/${nonIntroSteps.length}`,
      label: step.label || undefined,
    };
  };

  console.log('[Frame140] Session data:', {
    completedTasks: session.completedTasks,
    taskProgress: session.taskProgress,
  });

  const allTasks = [
    {
      id: 'about',
      title: 'Setup Your Plan',
      description: 'Add your personal details, trusted loved ones, and important passwords.',
      status: isCompleteFlow('about') ? 'completed' : 'next',
      category: 'Personal',
      icon: 'heart',
      route: '/frame-132',
      summaryRoute: '/frame-135',
    },
    {
      id: 'legal_essentials',
      title: 'Complete Your Legal Essentials',
      description: 'Upload important legal documents and choose your executor.',
      status: isCompleteFlow('legal_essentials') ? 'completed' : 'next',
      category: 'Legal',
      icon: 'bank',
      route: '/frame-260',
      summaryRoute: '/frame-215',
    },
    {
      id: 'personal',
      title: 'Organize Personal Accounts and Belongings',
      description: 'Document personal accounts, belongings, and instructions for loved ones.',
      category: 'Personal',
      status: isCompleteFlow('personal') ? 'completed' : 'next',
      icon: 'bank',
      route: '/frame-800',
      summaryRoute: '/frame-802',
    },
    {
      id: 'pets',
      title: 'Plan for Your Pets and Dependents',
      description: 'Document care plans for those who depend on you.',
      status: isCompleteFlow('pets') ? 'completed' : 'next',
      category: 'Personal',
      icon: 'heart',
      route: '/frame-241',
      summaryRoute: '/frame-248',
    },
    {
      id: 'funeral',
      title: 'Plan for Your Funeral and Memorial',
      description: 'Share your preferences for your funeral, memorial, or celebration of life.',
      category: 'Personal',
      status: isCompleteFlow('funeral') ? 'completed' : 'next',
      icon: 'heart',
      route: '/frame-219',
      summaryRoute: '/frame-223',
    },
    {
      id: 'inventory',
      title: 'Inventory of Assets',
      description: 'Create a clear picture of your financial accounts and assets.',
      category: 'Financial',
      status: isCompleteFlow('inventory') ? 'completed' : 'next',
      icon: 'bank',
      route: '/frame-174',
      summaryRoute: '/frame-213',
    },
    {
      id: 'debts',
      title: 'Track Debts and Final Expenses',
      description: 'Keep track of outstanding debts and expected final expenses.',
      category: 'Financial',
      status: isCompleteFlow('debts') ? 'completed' : 'next',
      icon: 'bank',
      route: '/frame-234',
      summaryRoute: '/frame-238',
    },
    {
      id: 'distribution',
      title: 'Assign and Approve Asset Distribution',
      description: 'Outline how assets should be distributed and approved.',
      category: 'Financial',
      status: isCompleteFlow('distribution') ? 'completed' : 'next',
      icon: 'bank',
      route: '/frame-245',
      summaryRoute: '/frame-247',
    },
    {
      id: 'legacy',
      title: 'Leave Behind Memories and Messages',
      description: 'Assemble a digital keepsake for your family and loved ones.',
      status: isCompleteFlow('legacy') ? 'completed' : 'next',
      category: 'Personal',
      icon: 'heart',
      route: '/frame-157',
      summaryRoute: '/frame-173',
    },
  ];

  const allTasksComplete = allTasks.every((task) => task.status === 'completed');
  const completedTasksCount = allTasks.filter((t) => t.status === 'completed').length;
  const totalTasks = allTasks.length;
  const completionPercentage = Math.round((completedTasksCount / totalTasks) * 100);

  console.log('[Frame140] Task status summary:', {
    allTasks: allTasks.map((t) => ({ id: t.id, status: t.status })),
    completedTasks: completedTasksCount,
    totalTasks,
    completionPercentage,
    allTasksComplete,
  });

  useEffect(() => {
    flowIds.forEach((fid) => {
      if (isCompleteFlow(fid)) {
        void reconcileFlowCompletion(fid);
      }
    });
  }, [flowIds, reconcileFlowCompletion, session.completedTasks, session.taskProgress]);

  useEffect(() => {
    if (completionPercentage >= 20 && completionPercentage < 30 && !hasShown20PercentModal) {
      setShowProgressModal(true);
      setHasShown20PercentModal(true);
      localStorage.setItem('hasShown20PercentModal', 'true');
    }
  }, [completionPercentage, hasShown20PercentModal]);

  useEffect(() => {
    const fetchAllFlowProgress = async () => {
      setFlowProgressLoading(true);

      try {
        const results = await Promise.allSettled(
          flowIds.map((flowId) => apiService.request(`/api/flows/${flowId}/progress`, { method: 'GET' }))
        );

        const progressData: Partial<Record<FlowId, FlowProgress>> = {};

        results.forEach((result, index) => {
          const flowId = flowIds[index];
          if (result.status === 'fulfilled' && result.value?.success) {
            progressData[flowId] = {
              lastFrame: result.value.lastFrame,
              updatedAt: result.value.updatedAt,
            };
          } else {
            progressData[flowId] = { lastFrame: null, updatedAt: null };
          }
        });

        setFlowProgress(progressData);

        console.log('[Frame140] Articles:', articles);
      } catch (err) {
        console.error('[Frame140] Failed to fetch flow progress:', err);
      } finally {
        setFlowProgressLoading(false);
      }
    };

    fetchAllFlowProgress();
  }, [flowIds]);

  const reviewTask = {
    id: 'review',
    title: 'Review and Finalize',
    subtitle: 'Take a look at your plan',
    status: 'next',
    category: 'Legal',
    icon: 'check',
    route: '/frame-141',
  };

  const planAccessTask = {
    id: 'planAccess',
    title: 'Plan Access',
    subtitle: 'Share your plan with trusted family members or your executor',
    status: 'next',
    category: 'Personal',
    icon: 'check',
    route: '/frame-301',
    summaryRoute: '/frame-304',
  };

  const planAccessComplete = session.completedTasks?.includes('confirm_plan_access') ?? false;

  const toggleFilter = (category: string) => {
    setActiveFilters((prev) => (prev.includes(category) ? prev.filter((f) => f !== category) : [...prev, category]));
  };

  const filterTasks = (tasks: unknown[]) => {
    if (activeFilters.length === 0) return tasks;
    return tasks.filter((task) => activeFilters.includes(task.category));
  };

  const getResumeFrame = (taskId: string): number => {
    const flowId = taskId as FlowId;
    const progress = flowProgress[flowId];
    return resolveResumeFrame(flowId, progress?.lastFrame);
  };

  const getSummaryFrameForTask = (taskId: string): number => {
    return getSummaryFrame(taskId as FlowId);
  };

  const getEntryFrameForTask = (taskId: string): number => {
    return getEntryFrame(taskId as FlowId);
  };

  const normalizeDashboardText = (value?: string) => value?.trim() ?? '';

  const isTaskInProgress = (flowId: FlowId): boolean => {
    return flowProgress[flowId]?.lastFrame != null;
  };

  const navigateToResume = (taskId: string) => {
    const resumeFrame = getResumeFrame(taskId);
    navigate(`/frame-${resumeFrame}`);
  };

  const navigateToSummary = (taskId: string) => {
    const summaryFrame = getSummaryFrameForTask(taskId);
    navigate(`/frame-${summaryFrame}`);
  };

   const navigateToEntry = (taskId: string) => {
    const summaryFrame = getEntryFrameForTask(taskId);
    navigate(`/frame-${summaryFrame}`);
  };

  return (
    <>
      <PlannerDashboardTour shouldRun={shouldRunTour} />
      <div data-tour="planner-dashboard" className="w-full max-w-[1200px] mx-auto px-6 py-6 bg-white rounded-lg border-2 border-cyan-600">
        <div className="flex flex-col">
            <div className="w-full max-w-[694px] mx-auto">
              <h2 className="font-display text-xl font-semibold text-stone-800 -mt-2 text-center break-words">
                {session.name ? `${session.name}'s Estate Plan` : "Your Estate Plan"}
              </h2>

              {/* Overall Progress - responsive width, no horizontal padding for alignment */}
              <div data-tour="planner-progress" className="mt-4 mb-6 w-full rounded-lg py-4 flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="font-semibold font-sans text-green-600">
                    {completionPercentage}% completed
                  </span>
                  <p className="font-semibold font-sans text-stone-600">
                    {completedTasksCount} of {totalTasks} steps
                  </p>
                </div>

                {/* Long Progress Bar */}
                <div className="w-full">
                  <div className="h-1 w-full bg-stone-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-600 transition-all duration-500 ease-out rounded-full"
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

        {/* Dynamic headline based on completion */}
        <p className="font-display text-xl font-semibold text-cyan-900 mb-5 text-start">
            { fromOnboarding ? "Getting Started" : 
              allTasksComplete ? "Your plan is complete—for now." 
              : "Ok, let's keep it going. Here's your plan so far."
            }
          </p>
          
          <p className="text-sm text-stone-600 mb-5 font-semibold max-w-[894px] text-start">
            { fromOnboarding ? "Complete your estate plan step-by-step with confidence and let End of an Era guide you through the process" : 
            allTasksComplete ? "Life changes, and your plan can change with it. You can return anytime to review or update details so everything stays accurate for the people who may rely on it." 
            : ""
            }
          </p>

        {/* Filters + Workflow Tiles */}
        <div data-tour="planner-workflow-tiles" className="w-full max-w-[894px]">
        <div className="flex justify-start gap-3 mb-4 pr-4">
          {['Personal','Legal','Financial'].map(cat => {
            const isActive = activeFilters.includes(cat);

            return (
              <button
                key={cat}
                onClick={() => toggleFilter(cat)}
                className={`
                  px-4 py-2 rounded-md text-xs font-medium transition-all duration-200 border border-stone-300
                  ${isActive 
                    ? 'bg-cyan-600 text-white'
                    : 'bg-stone-50 text-black hover:bg-neutral-300'
                  }
                `}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* Next up - Show Review and Finalize if all main tasks complete, otherwise show incomplete main tasks */}
        <p className="text-xs font-semibold text-black mb-2">Next up</p>
        
        {allTasksComplete ? (
          // Show Review and Finalize task (only if Legal category is active AND all tasks complete)
          (
          <div className="mb-6 max-w-[894px] w-full bg-green-700 rounded-lg border border-cyan-900 p-4 flex items-start gap-3 cursor-pointer"
            onClick={() => navigate(reviewTask.route)}
            role="button"
            tabIndex={0}
          >
            <div className="flex-1">
              <p className="font-sans text-lg font-semibold text-stone-50">{normalizeDashboardText(reviewTask.title)}</p>
              <p className="text-sm text-stone-50 mt-1">{normalizeDashboardText(reviewTask.subtitle)}</p>
            </div>
            </div>
          )
        ) : (
          // Show incomplete main tasks (filtered)
          filterTasks(allTasks.filter(t=>t.status==='next')).map(task=> {
            const inProgress = isTaskInProgress(task.id);
            return (
          <div
            key={task.id}
            className="mb-6 w-full max-w-[894px] bg-white border border-stone-400 rounded-lg shadow-[0_3px_10px_4px_rgba(139,177,114,0.20)] p-4 overflow-hidden"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
              <div className="flex min-w-0 flex-1 gap-3">
                {renderLeftIcon(task.icon,'w-5 h-5 flex-shrink-0 text-cyan-700 mt-0.5')}
                <div className="min-w-0 flex-1">
                  <p className="font-sans text-lg font-semibold text-stone-800">{normalizeDashboardText(task.title)}</p>
                  {inProgress &&
                    (() => {
                      const meta = getInProgressStepMeta(task.id as FlowId);
                      return (
                        <span className="inline-flex items-center gap-2 text-[10px] px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-medium mt-1 mr-2">
                          <span>
                            <span className="font-extrabold">In Progress</span> • {meta?.stepText ?? 'Intro'}
                          </span>
                          {meta?.label ? <span className="italic">• {meta.label}</span> : null}
                        </span>
                      );
                    })()}
                  <p className="text-xs text-stone-500 mt-1">{normalizeDashboardText(task.subtitle)}</p>
                  {task.description && (
                    <p className="text-sm text-stone-800 mt-2">{normalizeDashboardText(task.description)}</p>
                  )}
                </div>
              </div>
              <div className="flex w-full shrink-0 items-center justify-end gap-2 sm:w-auto sm:self-start sm:ml-auto">
                <span className="text-[12px] px-2 py-0.5 rounded-xl bg-neutral-200 text-black whitespace-nowrap">
                  {task.category}
                </span>
                <button 
                  onClick={() => navigateToResume(task.id)} 
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-md transition-colors flex-shrink-0"
                  aria-label={inProgress ? "Continue" : "Start"}
                >
                  {inProgress ? 'Continue' : 'Start'}
                </button>
              </div>
            </div>
          </div>
          );
        })
        )}
        </div>{/* end planner-workflow-tiles */}

        {/* Plan Access*/}
        <div
          data-tour="planner-plan-access"
          className={`mt-10 mb-6 w-full max-w-[894px] rounded-lg p-4 overflow-hidden ${
            planAccessComplete
              ? 'bg-white border border-neutral-200'
              : 'bg-white border border-stone-400 shadow-[0_3px_10px_4px_rgba(139,177,114,0.20)]'
          }`}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
            <div className="flex min-w-0 flex-1 gap-3">
              {planAccessComplete ? (
                renderLeftIcon('check', 'w-5 h-5 flex-shrink-0 text-green-700 mt-0.5')
              ) : (
                renderLeftIcon(planAccessTask.icon, 'w-5 h-5 flex-shrink-0 text-cyan-700 mt-0.5')
              )}
              <div className="min-w-0 flex-1">
                <p className="font-sans text-lg font-semibold text-stone-800">{normalizeDashboardText(planAccessTask.title)}</p>
                {planAccessComplete ? (
                  <p className="text-sm text-stone-800 mt-1">Completed - Review summary or make edits</p>
                ) : (
                  <p className="text-sm text-stone-800 mt-2">{normalizeDashboardText(planAccessTask.subtitle)}</p>
                )}
              </div>
            </div>
            <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:self-start sm:ml-auto">
              <span className="text-[12px] px-2 py-0.5 rounded-xl bg-neutral-200 text-black whitespace-nowrap">
                {planAccessTask.category}
              </span>
              {planAccessComplete ? (
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate(planAccessTask.route)}
                    className="px-3 py-1.5 text-sm font-medium text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded-md transition-colors flex items-center gap-1"
                    aria-label="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(planAccessTask.summaryRoute)}
                    className="px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm font-medium rounded-md transition-colors"
                    aria-label="View Summary"
                  >
                    View Summary
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(planAccessTask.route)}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-md transition-colors"
                  aria-label="Start"
                >
                  Start
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Completed */}
        <p className="text-xs font-medium text-stone-800 mt-10 mb-2">Completed</p>
        {/* Completed main tasks */}
        {filterTasks(allTasks.filter(t => t.status === 'completed')).map(task => (
          <div key={task.id} className="w-full max-w-[894px] bg-white border border-neutral-200 rounded-lg p-4 mb-6 overflow-hidden">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
              <div className="flex min-w-0 flex-1 gap-3">
                {renderLeftIcon('check','w-5 h-5 flex-shrink-0 text-green-700')}
                <div className="min-w-0 flex-1">
                  <p className="font-sans text-lg font-semibold text-black">{task.title}</p>
                  <p className="text-sm text-stone-800 mt-1">Completed - Review summary or make edits</p>
                </div>
              </div>
              <div className="flex w-full shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto sm:self-start sm:ml-auto">
                <span className="text-[12px] px-2 py-0.5 rounded-xl bg-neutral-200 text-black whitespace-nowrap">{task.category}</span>
                <div className="flex gap-2 flex-shrink-0">
                  <button 
                    onClick={() => navigateToEntry(task.id)} 
                    className="px-3 py-1.5 text-sm font-medium text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded-md transition-colors flex items-center gap-1"
                    aria-label="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button 
                    onClick={() => navigateToSummary(task.id)} 
                    className="px-3 py-1.5 bg-stone-100 hover:bg-stone-200 text-stone-800 text-sm font-medium rounded-md transition-colors"
                    aria-label="View Summary"
                  >
                    View Summary
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>

      <KBArticleSection />

      <ProgressCheckModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        completionPercent={completionPercentage}
        userName={session.name || ''}
        onFeeling={(feeling) => {
          console.log('[Frame140] User feeling:', feeling);
          setShowProgressModal(false);
        }}
      />

      <Modal
        open={!!selectedArticle}
        onClose={() => setSelectedArticle(null)}
        title={selectedArticle?.title || ''}
        cancelText="Close"
      >
        {selectedArticle && (
          <div className="text-stone-600 [&_h1]:text-stone-800 [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-stone-800 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-2 [&_h3]:text-stone-800 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_p]:my-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2 [&_li]:my-1 [&_a]:text-teal-600 [&_a]:underline [&_strong]:font-bold [&_strong]:text-stone-800 [&_em]:italic [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:rounded [&_pre]:overflow-x-auto [&_blockquote]:border-l-4 [&_blockquote]:border-teal-500 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:bg-gray-50 [&_blockquote]:py-2 [&_hr]:my-4 [&_hr]:border-gray-300">
            <ReactMarkdown remarkPlugins={[remarkBreaks]} rehypePlugins={[rehypeRaw]}>
              {selectedArticle.content.external}
            </ReactMarkdown>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Frame140;